import { AuthErrorCodes as FirebaseErrorCodes } from 'firebase/auth'
import { ErrorCodes } from '@/utils/errorCodes'
import { checkGoogleApiNetwork } from '@/utils/firebase/googleApiDiagnostics/networkAvailability'
import { isGoogleApiScriptLoadable } from '@/utils/firebase/googleApiDiagnostics/scriptAvailability'

// The only entry point into these diagnostics - nothing outside this directory should import its other modules.
// Everything here exists purely to explain a Firebase `auth/internal-error` to the customer, so the whole feature
// can be dropped by deleting this directory and its single call site in `firebaseUiAuth`.

// Firebase reports a blocked Google API script as `auth/internal-error`, which it also returns for a long list of
// unrelated reasons. Given such a failure, this narrows down whether access to the Google API domain is the cause
// and which specific advice the customer needs. Returns null when the failure is not one we can explain, in which
// case the caller should keep whatever message it already has.
export const diagnoseFederatedAuthFailure = async (error: unknown): Promise<ErrorCodes | null> => {
  if (!isFirebaseInternalError(error)) {
    return null
  }

  // The network path is checked first, as a blocked network fails the script check too and would otherwise be
  // misreported to the customer as a browser problem.
  const networkStatus = await checkGoogleApiNetwork()

  if (networkStatus === 'blocked') {
    return ErrorCodes.FederatedAuthNetworkBlocked
  }

  // Our URL has gone stale, so the script check below would fail for a reason that has nothing to do with the
  // customer. Explain nothing rather than send them looking for a problem they do not have.
  if (networkStatus === 'urlNotFound') {
    return null
  }

  if (!(await isGoogleApiScriptLoadable())) {
    return ErrorCodes.FederatedAuthScriptBlocked
  }

  return null
}

interface FirebaseErrorWithCode extends Error {
  code?: string
}

// FirebaseUI has no message of its own for `auth/internal-error`, so it renders Firebase's raw message, which
// carries the code - e.g. `Firebase: An internal AuthError has occurred. (auth/internal-error).` A substring match
// keeps this working whatever wrapping the message picks up on the way through.
const isFirebaseInternalError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false
  }

  const { code, message } = error as FirebaseErrorWithCode

  return code === FirebaseErrorCodes.INTERNAL_ERROR || message.includes(FirebaseErrorCodes.INTERNAL_ERROR)
}
